/* eslint-disable @typescript-eslint/no-explicit-any */
import DataTable from "@/components/data-table";
import { ColumnVisibility } from "@/components/data-table/column-visiblity";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { usePermission } from "@/hooks/use-premission";

import { PublishLinksDialog } from "@/components/popups/publish-links-dialog";
import { RunPlacementTest } from "@/components/popups/run-placement-test";
import SchedulePlacementTest from "@/components/popups/shedule-placement-test";
import { Badge } from "@/components/ui/badge";
import { SearchInput } from "@/components/ui/search-input";
import {
  useGetCardSummary,
  useGetInboxPlacements,
  usePublishAllInboxPlacementReport,
  useUnPublishAllInboxPlacementReport,
} from "@/hooks/use-inboxplacement";
import { useGetUsage, useGetAllowedEspTypes } from "@/hooks/use-subscription";
import { useUsageLimitChecker } from "@/hooks/use-usage-limit-checker";
import {
  b2bColumnDefs,
} from "@/pages/inbox-placement/placement-table.columns";
import { useServices } from "@/services/hooks/useServices";
import { PlacementType } from "@/types/inboxPlacements.types";
import { ClockUser } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { AgGridReact } from "ag-grid-react";
import {
  BarChart3,
  Download,
  EllipsisVertical,
  FileDown,
  FileUp,
  Inbox,
  Loader2,
  MailWarning,
  OctagonAlert,
  RefreshCcw,
} from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

// Coin Icon Component
function CoinIcon({ 
  available, 
  max 
}: { 
  available: number; 
  max: number 
}) {
  // Get color based on available tests percentage
  const getColor = () => {
    if (max === 0) return "#ef4444"; // red
    const percentage = (available / max) * 100;
    if (percentage <= 25) return "#ef4444"; // red
    if (percentage <= 50) return "#eab308"; // yellow
    if (percentage <= 75) return "#22c55e"; // green
    return "#22c55e"; // green
  };

  const color = getColor();

  return (
    <div className="relative h-6 w-6">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-full w-full"
      >
        {/* Coin circle */}
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke={color}
          strokeWidth="2"
          fill={color}
          fillOpacity="0.2"
        />
        {/* Coin inner circle for depth */}
        <circle
          cx="12"
          cy="12"
          r="7"
          stroke={color}
          strokeWidth="1.5"
          fill="none"
        />
        {/* Dollar sign or coin symbol */}
        <text
          x="12"
          y="16"
          fontSize="10"
          fontWeight="bold"
          fill={color}
          textAnchor="middle"
          fontFamily="Arial, sans-serif"
        >
          $
        </text>
      </svg>
    </div>
  );
}

import InitialPageVisitGuideModal from "@/components/page-visit-guide";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Route = createFileRoute("/_private/placement-test/")({
  component: RouteComponent,
  validateSearch: () => {
    return {
      type: PlacementType.B2B,
    };
  },
});

function RouteComponent() {
  const gridRef = useRef<AgGridReact>(null);

  const activeTab = PlacementType.B2B;

  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [showLinksDialog, setShowLinksDialog] = useState(false);
  const [showRunTestDialog, setShowRunTestDialog] = useState(false);
  const [showResults, setShowResults] = useState(true);
  const [showScheduleEmailTest, setShowScheduleEmailTest] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(100);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { hasPermission } = usePermission();
  const { inboxPlacementService } = useServices();
  const { isUsageLimitReached } = useUsageLimitChecker();

  const { data: placementData, isLoading } = useGetInboxPlacements({
    type: activeTab,
    limit: limit,
    page: currentPage,
  });

  const { data: cardSummary } = useGetCardSummary(activeTab);
  const { data: usageData } = useGetUsage();
  const { data: allowedEspData } = useGetAllowedEspTypes();

  // Map API ESP types to column field names
  const espTypeToColumnFieldMap: Record<string, string[]> = {
    googleworkspace: ["googleworkspace"],
    microsoft365: ["microsoft365"],
    zoho: ["zoho"],
    abusix: ["amazon abusix", "amazonAbuseEx"],
    spamhaus: ["amazonSpamhaus"],
    trendmicro: ["amazonTrendMicro"],
    rspamd: ["rspamd"],
    gmail: ["gmail"],
    outlook: ["outlook"],
    aol: ["aol"],
    icloud: ["icloud"],
  };

  // Filter columns based on allowed ESP data
  const filteredColumnDefs = useMemo(() => {
    if (!allowedEspData) {
      return b2bColumnDefs;
    }

    // If all ESPs are allowed, show all columns
    if (allowedEspData.allEspAllowed || allowedEspData.allowedEspTypes.length === 0) {
      return b2bColumnDefs;
    }

    // Create a set of allowed column field names
    const allowedFields = new Set<string>();
    allowedEspData.allowedEspTypes.forEach((espType) => {
      const fields = espTypeToColumnFieldMap[espType.toLowerCase()];
      if (fields) {
        fields.forEach((field) => allowedFields.add(field));
      }
    });

    // Base column header names that should always be shown
    const baseColumnHeaders = ["Email", "Tested Providers", "Score", "Created At", "ESP Type"];

    // Filter columns: keep base columns and only ESP columns that are allowed
    return b2bColumnDefs.filter((col) => {
      // Always keep base columns (identified by headerName)
      if (col.headerName && baseColumnHeaders.includes(col.headerName)) {
        return true;
      }

      // For ESP columns, check if the field is in allowedFields
      if (col.field) {
        return allowedFields.has(col.field);
      }

      // If no field and not a base column, exclude it
      return false;
    });
  }, [allowedEspData]);

  const {
    mutateAsync: publishDomainReport,
    data: publishedLinksData,
    isPending: isPublishing,
  } = usePublishAllInboxPlacementReport();

  const { mutateAsync: unPublishDomainReport, isPending: isUnPublishing } =
    useUnPublishAllInboxPlacementReport();

  const handlePublishDomain = async (type: "publish" | "unpublish") => {
    try {
      const emails = selectedRows.map((row) => row.email);
      if (type === "publish") {
        await publishDomainReport({
          emails,
          type: activeTab,
        });

        setShowLinksDialog(true);
      } else {
        await unPublishDomainReport({
          emails,
          type: activeTab,
        });
      }

      const eventName =
        type === "publish"
          ? "inbox_placement_published_all"
          : "inbox_placement_unpublished_all";

      window.cioanalytics?.track(eventName, {
        count: emails.length,
        emails,
        type: activeTab,
      });

      setSelectedRows([]);
      gridRef.current?.api.deselectAll();

      queryClient.invalidateQueries({
        queryKey: ["inboxplacements", { type: PlacementType.B2B, page: 1, limit: 100 }],
      });

      toast.success(
        `Inbox placement report ${
          type === "publish" ? "published" : "unpublished"
        } successfully`
      );
    } catch (error) {
      toast.error(
        `Failed to ${
          type === "publish" ? "publish" : "unpublish"
        } inbox placement report`
      );
    }
  };

  const handleRowClick = (params: any) => {
    console.log(params.data, "activeTab", activeTab);
    navigate({
      to: `/placement-test/${params.data.email}`,
      search: {
        type: PlacementType.B2B,
        _testId: params.data.testId,
      },
    });
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const response = await inboxPlacementService.exportPlacementSummaryGrid(
        activeTab
      );
      const blob = new Blob([response as unknown as BlobPart], {
        type: "text/csv",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Inbox-placement.csv";
      a.click();

      window.cioanalytics?.track("inbox_placement_exported", {
        type: activeTab,
      });
    } catch (error) {
      toast.error("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const summaryData = useMemo(() => {
    if (!cardSummary) {
      return {
        averageScore: 0,
        inboxPercent: 0,
        inboxCount: 0,
        undeliveredPercent: 0,
        undeliveredCount: 0,
        spamPercent: 0,
        spamCount: 0,
        totalEmails: 0,
        totalPlacements: 0,
      };
    }

    return {
      averageScore: cardSummary.averageScore || 0,
      inboxPercent: cardSummary.inboxPlacement || 0,
      inboxCount: Object.values(cardSummary.providerStats || {}).reduce(
        (sum, provider) => sum + (provider.inbox || 0),
        0
      ),
      undeliveredPercent: cardSummary.undelivered || 0,
      undeliveredCount: Object.values(cardSummary.providerStats || {}).reduce(
        (sum, provider) => sum + (provider.undelivered || 0),
        0
      ),
      spamPercent: cardSummary.spamPlacement || 0,
      spamCount: Object.values(cardSummary.providerStats || {}).reduce(
        (sum, provider) => sum + (provider.spam || 0),
        0
      ),
      totalEmails: cardSummary.totalTests || 0,
      totalPlacements: Object.values(cardSummary.providerStats || {}).reduce(
        (sum, provider) => sum + (provider.total || 0),
        0
      ),
      // averageScore: 95,
      // inboxPercent: 95,
      // inboxCount: 1900,
      // undeliveredPercent:  0,
      // undeliveredCount: 0,
      // spamPercent: 5,
      // spamCount: 100,
      // totalEmails: 500,
      // totalPlacements: 2000,
    };
  }, [cardSummary]);

  const onFilterTextBoxChanged = useCallback(
    (searchTerm: string) => {
      if (gridRef.current?.api) {
        gridRef.current.api.setGridOption("quickFilterText", searchTerm);
      }
    },
    [gridRef]
  );

  const handleOpenRunTestDialog = () => {
    if (isUsageLimitReached("limit_inboxPlacementTestsUsedThisMonth")) {
      toast.error(
        "You have reached the limit of maximum inbox placement tests for this month."
      );
      return;
    }

    setShowRunTestDialog(true);
  };

  return (
    <div className="w-full">
      <InitialPageVisitGuideModal type="inboxPlacement" />

      <div className="flex flex-col lg:flex-row justify-between lg:items-center mb-4 gap-2">
        <div>
          <h1 className="page-heading">Inbox Placement Test</h1>
          <p className="text-muted-foreground max-w-lg w-full">
            Test where your emails land across B2B and B2C email providers
          </p>
        </div>
        <div className="flex flex-col xl:flex-row xl:items-center lg:items-end gap-2">
          <div className="flex items-center gap-2">
            <CoinIcon
              available={Math.max(
                (usageData?.maxInboxPlacementTestsUsedThisMonth || 0) -
                  (usageData?.inboxPlacementTestsUsedThisMonth || 0),
                0
              )}
              max={usageData?.maxInboxPlacementTestsUsedThisMonth || 0}
            />
            <div className="text-sm font-medium">
              Available Inbox Placement Tests:
            </div>
            <div className="text-sm text-muted-foreground">
              {Math.max(
                (usageData?.maxInboxPlacementTestsUsedThisMonth || 0) -
                  (usageData?.inboxPlacementTestsUsedThisMonth || 0),
                0
              )}
            </div>
          </div>
        </div>
      </div>

      {showResults && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="border-s-4 border-s-blue-500 bg-blue-50 dark:bg-blue-900/20">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium  text-blue-900 dark:text-blue-400">
                      Average Score
                    </p>
                    <h3
                      className={`text-3xl font-bold text-blue-900 dark:text-blue-400`}
                    >
                      {summaryData.averageScore}%
                    </h3>
                    <p className="text-xs text-blue-700 mt-1 dark:text-blue-400">
                      Based on {summaryData.totalEmails} tests
                    </p>
                  </div>
                  <BarChart3 className="h-6 w-6 text-blue-500 dark:text-blue-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-s-4 border-s-green-500 bg-green-50 dark:bg-green-900/20">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium  text-green-900 dark:text-green-400">
                      Inbox Placement
                    </p>
                    <h3 className="text-3xl font-bold text-green-500 dark:text-green-400">
                      {summaryData.inboxPercent}%
                    </h3>
                    <p className="text-xs text-green-700 mt-1 dark:text-green-400">
                      {summaryData.inboxCount} of {summaryData.totalPlacements}{" "}
                      placements
                    </p>
                  </div>
                  <Inbox className="h-6 w-6 text-green-500 dark:text-green-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-s-4 border-s-amber-500 bg-amber-50 dark:bg-amber-900/20">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium  text-amber-900 dark:text-amber-400">
                      Undelivered
                    </p>
                    <h3 className="text-3xl font-bold text-amber-500 dark:text-amber-400">
                      {summaryData.undeliveredPercent}%
                    </h3>
                    <p className="text-xs text-amber-700 mt-1 dark:text-amber-400">
                      {summaryData.undeliveredCount} of{" "}
                      {summaryData.totalPlacements} placements
                    </p>
                  </div>
                  <MailWarning className="h-6 w-6 text-amber-500 dark:text-amber-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-s-4 border-s-red-500 bg-red-50 dark:bg-red-900/20">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium  text-red-900 dark:text-red-400">
                      Spam Placement
                    </p>
                    <h3 className="text-3xl font-bold text-red-500 dark:text-red-400">
                      {summaryData.spamPercent}%
                    </h3>
                    <p className="text-xs text-red-700 mt-1 dark:text-red-400">
                      {summaryData.spamCount} of {summaryData.totalPlacements}{" "}
                      placements
                    </p>
                  </div>
                  <OctagonAlert className="h-6 w-6 text-red-500 dark:text-red-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4 mb-4">
            <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-2">
              <SearchInput
                onSearch={onFilterTextBoxChanged}
                placeholder="Search by emails..."
                className="max-w-none sm:w-full md:max-w-[300px]"
              />
              <div className="flex items-center gap-2 justify-end flex-wrap md:flex-nowrap">
                {selectedRows.length > 0 && (
                  <>
                    <div className="text-sm text-muted-foreground flex items-center gap-2 px-2">
                      <Badge variant="default">{selectedRows.length}</Badge>
                      <span>rows selected</span>
                    </div>
                    <Button
                      onClick={() => {
                        setSelectedRows([]);
                        gridRef.current?.api.deselectAll();
                      }}
                      className="flex items-center gap-2"
                    >
                      Deselect
                    </Button>
                  </>
                )}

                {hasPermission("inbox_placement:run_test") &&
                  selectedRows.length === 0 && (
                    <Button
                      onClick={handleOpenRunTestDialog}
                      className="flex items-center gap-2"
                    >
                      <RefreshCcw size={16} />
                      Run New Test
                    </Button>
                  )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <EllipsisVertical className="h-4 w-4" />
                      <span className="hidden xs:block">Actions</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {hasPermission("inbox_placement:manage_schedule") && (
                      <DropdownMenuItem
                        onSelect={() => setShowScheduleEmailTest(true)}
                      >
                        <ClockUser className="w-4 h-4" />
                        Schedule Test
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuItem
                      onSelect={() => handleExport()}
                      disabled={
                        isExporting ||
                        placementData?.summary?.tests?.length === 0
                      }
                    >
                      {isExporting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Exporting...</span>
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" />
                          <span>Export</span>
                        </>
                      )}
                    </DropdownMenuItem>

                    {hasPermission("inbox_placement:edit") && (
                      <>
                        <DropdownMenuItem
                          onSelect={() => handlePublishDomain("publish")}
                          disabled={isPublishing || selectedRows.length === 0}
                        >
                          {isPublishing ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Publishing...</span>
                            </>
                          ) : (
                            <>
                              <FileUp className="h-4 w-4" />
                              Publish All
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => handlePublishDomain("unpublish")}
                          disabled={isUnPublishing || selectedRows.length === 0}
                        >
                          {isUnPublishing ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Unpublishing...</span>
                            </>
                          ) : (
                            <>
                              <FileDown className="h-4 w-4" />
                              Unpublish All
                            </>
                          )}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                <ColumnVisibility
                  gridRef={gridRef}
                  storageKey="placement-test-b2b-columns-state"
                  triggerClassName="[&>span]:hidden xs:[&>span]:block"
                />
              </div>
            </div>
          </div>

          <DataTable
            rowData={placementData?.summary?.tests || []}
            columnDefs={filteredColumnDefs}
            loading={isLoading}
            defaultColDef={{
              sortable: true,
              filter: true,
              editable: false,
            }}
            onRowClicked={handleRowClick}
            onSelectionChanged={(event) => {
              setSelectedRows(event.api.getSelectedRows());
            }}
            ref={gridRef}
            rowHeight={60}
            height={550}
            className="w-full"
            showPagination={true}
            paginationProps={{
              pageSize: limit,
              totalPages: Math.ceil(
                (placementData?.summary.total || 0) / limit
              ),
              currentPage: currentPage,
              onPageChange: (page: number) => {
                setCurrentPage(page);
              },
              rowsPerPage: limit,
              onRowsPerPageChange: (limit: number) => {
                setLimit(limit);
              },
            }}
          />
        </>
      )}

      {showRunTestDialog && (
        <RunPlacementTest
          open={showRunTestDialog}
          allowedEspData={allowedEspData}
          onOpenChange={(open) => {
            setShowRunTestDialog(open);
            if (!open) {
              queryClient.invalidateQueries({
                queryKey: [
                  "inboxplacements",
                  { type: PlacementType.B2B, page: 1, limit: 100 },
                ],
              });
              queryClient.invalidateQueries({
                queryKey: ["inboxplacements", PlacementType.B2B],
              });
            }
          }}
        />
      )}

      {showScheduleEmailTest && (
        <SchedulePlacementTest
          isOpen={showScheduleEmailTest}
          onClose={() => setShowScheduleEmailTest(false)}
          initialSelectedDomains={selectedRows.map((row) => row.email)}
        />
      )}

      {publishedLinksData && (
        <PublishLinksDialog
          open={showLinksDialog}
          onOpenChange={setShowLinksDialog}
          links={publishedLinksData}
          type="inbox-placement"
          placementReportType={PlacementType.B2B}
        />
      )}
    </div>
  );
}
